import { useState } from "react";
import { Loader2, Hash, TrendingUp, Search, Database, HelpCircle, ExternalLink } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";

const intentColor = {
  informational: "#0055FF",
  transactional: "#10B981",
  commercial: "#F59E0B",
  navigational: "#A1A1AA",
};

export default function KeywordsPage() {
  const [seed, setSeed] = useState("");
  const [location, setLocation] = useState("United States");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/keywords/real-research", { seed, location });
      setResult(data);
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  const keywords = result?.result?.keywords || [];
  const paa = result?.serpapi_data?.people_also_ask || [];
  const organic = result?.serpapi_data?.organic_top || [];

  return (
    <DashboardLayout
      eyebrow="Keyword Research"
      title="Real keyword data — not guesses."
      subtitle="Live SERP scraping (SerpAPI) + real search volumes & CPC (DataForSEO). AI-classified intent."
    >
      <form onSubmit={submit} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 mb-6" data-testid="keywords-form">
        <div className="grid md:grid-cols-[1fr_220px_180px] gap-3 items-end">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Seed keyword</Label>
            <Input value={seed} onChange={(e) => setSeed(e.target.value)} required
                   placeholder="e.g. seo tools"
                   className="h-12 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="keywords-seed" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Location</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)}
                   className="h-12 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="keywords-location" />
          </div>
          <Button type="submit" disabled={loading} className="h-12 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="keywords-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Research</>}
          </Button>
        </div>
        {error && <div className="mt-4 text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="keywords-error">{error}</div>}
      </form>

      {loading && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm">
          {[...Array(6)].map((_, i) => <div key={i} className="h-14 border-b border-zinc-900 animate-pulse" />)}
        </div>
      )}

      {result && keywords.length > 0 && (
        <div className="space-y-6 fade-in" data-testid="keywords-result">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <span className="label-eyebrow">{keywords.length} keyword ideas</span>
                {result.source === "serpapi+dataforseo" && (
                  <Badge variant="outline" className="rounded-sm border-[#10B981]/40 text-[#10B981] text-[10px] tracking-widest uppercase">
                    <Database className="w-3 h-3 mr-1" /> Real data
                  </Badge>
                )}
              </div>
              <span className="text-xs text-zinc-500">{result.location}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800">
                    <th className="px-6 py-3 font-medium">Keyword</th>
                    <th className="px-6 py-3 font-medium">Volume</th>
                    <th className="px-6 py-3 font-medium">Difficulty</th>
                    <th className="px-6 py-3 font-medium">CPC</th>
                    <th className="px-6 py-3 font-medium">Competition</th>
                    <th className="px-6 py-3 font-medium">Intent</th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.map((k, i) => (
                    <tr key={i} className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/50 transition-colors" data-testid={`keyword-row-${i}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Hash className="w-3.5 h-3.5 text-zinc-600" />
                          <span className="text-zinc-200">{k.keyword}</span>
                          {k.difficulty < 30 && <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 rounded-sm text-[10px]">LOW COMP</Badge>}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-mono text-zinc-300">{Number(k.volume).toLocaleString()}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
                            <div className="h-full" style={{
                              width: `${k.difficulty}%`,
                              background: k.difficulty < 30 ? "#10B981" : k.difficulty < 60 ? "#F59E0B" : "#EF4444",
                            }} />
                          </div>
                          <span className="text-xs font-mono text-zinc-400">{k.difficulty}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-mono text-zinc-300">${Number(k.cpc).toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className={`text-xs uppercase tracking-widest ${k.competition_level === "LOW" ? "text-[#10B981]" : k.competition_level === "HIGH" ? "text-[#EF4444]" : "text-amber-400"}`}>
                          {k.competition_level || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-300 text-[10px] uppercase"
                               style={{ borderColor: (intentColor[k.intent] || "#71717A") + "40", color: intentColor[k.intent] || "#A1A1AA" }}>
                          {k.intent}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {paa.length > 0 && (
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="keywords-paa">
                <h3 className="font-display font-medium flex items-center gap-2 mb-3">
                  <HelpCircle className="w-4 h-4 text-[#3377FF]" /> People also ask
                </h3>
                <ul className="space-y-2">
                  {paa.map((q, i) => (
                    <li key={i} className="text-sm text-zinc-300 leading-relaxed flex items-start gap-2">
                      <span className="text-zinc-600 font-mono text-xs mt-0.5">{(i + 1).toString().padStart(2, "0")}</span>
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {organic.length > 0 && (
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="keywords-organic">
                <h3 className="font-display font-medium flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-[#3377FF]" /> Top ranking pages
                </h3>
                <ul className="space-y-3">
                  {organic.map((o, i) => (
                    <li key={i} className="text-sm">
                      <a href={o.link} target="_blank" rel="noreferrer" className="text-zinc-200 hover:text-[#3377FF] transition-colors flex items-start gap-2 group">
                        <span className="text-zinc-600 font-mono text-xs mt-0.5 w-5 text-right">#{o.position}</span>
                        <span className="line-clamp-2 leading-snug">{o.title}<ExternalLink className="w-3 h-3 inline-block ml-1 opacity-0 group-hover:opacity-100 transition-opacity" /></span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && (!result || keywords.length === 0) && (
        <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center text-zinc-500">
          <Hash className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
          <p>Enter a seed keyword to find ideas.</p>
        </div>
      )}
    </DashboardLayout>
  );
}
